<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

// ponytail: a 60s tick is enough resolution for a 24h window
const REFRESH_INTERVAL = 60000;
const WARNING_THRESHOLD_MINUTES = 120;

const timer = ref(null);
const now = ref(Date.now());
const isDismissed = ref(false);

const expiresAt = computed(() => props.chat?.messaging_window_expires_at);

const minutesRemaining = computed(() => {
  if (!expiresAt.value) return null;
  return Math.floor((expiresAt.value * 1000 - now.value) / 60000);
});

const isVisible = computed(
  () =>
    !isDismissed.value &&
    props.chat?.can_reply &&
    minutesRemaining.value !== null &&
    minutesRemaining.value >= 0
);

const isNearExpiry = computed(
  () => minutesRemaining.value <= WARNING_THRESHOLD_MINUTES
);

const timeRemaining = computed(() => {
  const minutes = minutesRemaining.value;
  if (minutes < 1) return t('CONVERSATION.MESSAGING_WINDOW.LESS_THAN_A_MINUTE');

  const hours = Math.floor(minutes / 60);
  if (!hours) return t('CONVERSATION.MESSAGING_WINDOW.MINUTES', { minutes });

  return t('CONVERSATION.MESSAGING_WINDOW.HOURS_AND_MINUTES', {
    hours,
    minutes: minutes % 60,
  });
});

const createTimer = () => {
  timer.value = setTimeout(() => {
    now.value = Date.now();
    createTimer();
  }, REFRESH_INTERVAL);
};

onMounted(createTimer);

onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);
});

// Dismissal is per conversation, so switching chats brings the banner back.
watch(
  () => props.chat?.id,
  () => {
    isDismissed.value = false;
    now.value = Date.now();
  }
);
</script>

<template>
  <Banner
    v-if="isVisible"
    :color="isNearExpiry ? 'amber' : 'slate'"
    :action-label="t('CONVERSATION.MESSAGING_WINDOW.DISMISS')"
    @action="isDismissed = true"
  >
    {{ t('CONVERSATION.MESSAGING_WINDOW.COUNTDOWN', { time: timeRemaining }) }}
  </Banner>
</template>
